import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PopoverController } from '@ionic/angular';
import Swal from 'sweetalert2';
import { IWorker } from 'src/app/models/IWorker.model';
import { DropdownService } from 'src/app/services/shared/dropdown.service';

/**
 * Popover "Cambia operatore" — aperto via PopoverController da:
 *  - PreparazioneOrdiniComponent (lista J.1)
 *  - PreparazioneOrdiniDetaggliComponent (modale dettagli J.3)
 *  - OrderDetailsComponent (block dentro J.3)
 *
 * Cleanup v2.11.0 J.1:
 * - Drop kendo-dropdownlist: native `<select>` Diakonix.
 * - Switch a `inject()` + `takeUntilDestroyed`.
 * - Drop 5 console.log (Item selected, Operators, Selected operator, Selected on change, Data).
 *
 * Strutturale: PopoverController Ionic mantenuto (deferred a Phase 2 con
 * full removal IonicModule). Il contenuto visual è ora Diakonix.
 */
@Component({
  selector: 'app-change-operator',
  templateUrl: './change-operator.component.html',
  styleUrls: ['./change-operator.component.scss'],
  standalone: false,
})


export class ChangeOperatorComponent implements OnInit {

  @Input() itemSelected: any;

  private destroyRef = inject(DestroyRef);
  private workerService = inject(DropdownService);
  public popoverController = inject(PopoverController);
  /**
   * 🚨 FIX v2.12.2: il componente vive dentro un Ionic PopoverController
   * — overlay isolato che non sempre triggera CD automatica al
   * subscribe.next. Loading flag toggle a false ma template restava su
   * `<spinner>` finché un evento DOM esterno non forzava un cycle.
   * Pattern ricorrente (4a volta dopo v2.11.1 J.3, v2.11.2 Q4 inventario,
   * v2.11.2 Q6 settings, v2.12.0 Q7 chat). Lint rule prevista in Phase 2.
   */
  private cdr = inject(ChangeDetectorRef);

  public formattedOperators: { full_name: string; account_id: number }[] = [];
  public selectedAccountId: number | null = null;
  public loading = false;
  public submitting = false;

  ngOnInit(): void {
    this.loadOperators();
  }

  private loadOperators(): void {
    this.loading = true;
    this.workerService
      .getWorkers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: IWorker[]) => {
          this.formattedOperators = (data ?? []).map((op) => ({
            full_name: `${op.ac_first_name} ${op.ac_last_name}`,
            account_id: op.account_id,
          }));
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  onSelectOperator(accountId: string | number): void {
    this.selectedAccountId = accountId === '' || accountId == null ? null : Number(accountId);
  }

  changeOperator(): void {
    if (!this.selectedAccountId || this.submitting) return;

    this.submitting = true;
    this.workerService
      .changeOperator(this.itemSelected?.order_id, this.selectedAccountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.submitting = false;
          if (data?.success) {
            Swal.fire({
              icon: 'success',
              title: 'Modifiche!',
              text: "Operatore aggiornato con successo!",
              timer: 1500,
              timerProgressBar: true,
            });
          }
          else {
            Swal.fire({
              icon: 'error',
              title: 'Errore!',
              text: "Impossibile cambiare l'operatore. Riprova.",
              timer: 1500,
              timerProgressBar: true,
            });
          }
          this.cdr.detectChanges();
          this.popoverController.dismiss();
        },
        error: () => {
          this.submitting = false;
          this.cdr.detectChanges();
          this.popoverController.dismiss();
        },
      });
  }

  cancel(): void {
    this.popoverController.dismiss();
  }
  
}
